/**
 * The Inbox page's URL state.
 *
 * Same rule as Contacts: the narrowed list IS the address. "Vis meg bare det jeg ikke har lest"
 * is a link a colleague can be sent, and a reload lands on the same list rather than on the
 * unfiltered one — so the filter writes to the address bar and not to a `useState`.
 *
 * Absent means default. `?unread=false` is never written, so the plain `/inbox` the menu links
 * to and the `/inbox` a person types are the same location and the router does not treat them
 * as two.
 *
 * =================================================================================================
 * TRE PARAMETERE KOM TIL I KOI DEL 4, og de sto tidligere her under «deliberately not», hver med
 * sin begrunnelse. Alle tre er innfridd i basen framfor omgått:
 *
 * * **`?q=`** sto som «`GET /v1/threads` has no search parameter … because no index answers one.
 *   Adding it starts with a migration.» Ruten har det nå (KOI Del 4): søket treffer emnet og
 *   meldingene i tråden, gjennom et semi-join som slår opp ÉN tråd per rad i den ordnede indeksen.
 * * **`?closed=`** sto som «no index both filters and orders by it». Migrering 0240 flyttet
 *   flagget ned på deltakerraden og la det inn i indeksens eget predikat — nettopp fordi et
 *   `EXISTS` mot `threads` gjorde fire plantester røde.
 * * **`?dm=`** er ny: avgjørelse 12 gir tråder en `dm_key`, og «Direktemeldinger» er en av
 *   ordrens fire faner.
 *
 * WHAT IS STILL DELIBERATELY NOT HERE:
 *
 * * **No `?sort=`.** The endpoint offers exactly one sort — last activity, descending — and
 *   refuses `direction=asc` in the schema, because the sort column is volatile and only
 *   descending is safe to page over. There is nothing to put in the URL.
 * * **No `?linkType=`/`?linkId=`.** The endpoint accepts the pair, and nothing on this page can
 *   set it. A parameter the reader has no control for is a feature outside the plan with no way
 *   in; it belongs to the contact/festival page that will one day deep-link into the inbox.
 *
 * =================================================================================================
 * OG `?thread=`, SOM ER DEN ENE SOM IKKE ER ET FILTER.
 *
 * Ordren: «valgt tråd til høyre (samme side, ikke ny rute)». Den valgte tråden er likevel en del av
 * ADRESSEN, fordi den er det siden viser: en kollega skal kunne få lenken til samtalen, og en
 * oppfriskning skal lande på den samme. Den ligger i søkestrengen og ikke i stien nettopp fordi
 * det ikke er en ny rute — lista står der hele tiden.
 *
 * `/inbox/$threadId` blir stående som dyplenke (varsler peker dit), og sender videre hit.
 */

/**
 * What `validateSearch` produces and what `navigate({ search })` must be handed back.
 *
 * `unread` og `dm` er `true` eller fraværende, aldri `false`: de to skrivemåtene ville vært den
 * samme lista under to adresser. `closed` er `true` eller fraværende av samme grunn — og fordi
 * standardsvaret fra ruten ER de åpne.
 */
export interface InboxSearch {
  unread?: true;
  dm?: true;
  closed?: true;
  /** «Bookinger i organisasjonen» (0931, 84): tilgang gjennom konteksten, uten å følge. */
  bookings?: true;
  q?: string;
  thread?: string;
  /**
   * DEN GAMLE VARSELFANEN. Fanen er borte (276.10: varslene står bare i bjella), men adressen finnes
   * i bokmerker og sendte lenker. Den leses fortsatt, så innboksen kan åpne bjella og skrive den om
   * (`withoutLegacyView`). Ingen kode skriver den lenger.
   */
  view?: 'notifications';
}

/** The spellings a checkbox-driven client produces, matching the API's own coercion. */
const TRUTHY: readonly string[] = ['true', '1', ''];

/** Søketeksten har et tak her OG i kontrakten. Det er den samme regelen, sagt til begge sider. */
const MAX_QUERY = 200;

/**
 * Validated at the edge of the router, like `validateContactsSearch` is: no component downstream
 * ever sees anything but the shapes above, whatever a hand-typed address said.
 *
 * FIRE FANER, ETT VALG. «Alle», «Uleste», «Direktemeldinger» og «Arkiverte» er gjensidig
 * utelukkende i menyen, men de er tre uavhengige parametere på ruten — og det er med vilje: den
 * som vil ha «uleste direktemeldinger» kan skrive adressen, og lista svarer riktig. Fanene setter
 * én om gangen; adressen tåler flere.
 */
export function validateInboxSearch(raw: Record<string, unknown>): InboxSearch {
  const search: InboxSearch = {};
  if (isTruthy(raw['unread'])) search.unread = true;
  if (isTruthy(raw['dm'])) search.dm = true;
  if (isTruthy(raw['closed'])) search.closed = true;
  if (isTruthy(raw['bookings'])) search.bookings = true;

  const q = raw['q'];
  if (typeof q === 'string') {
    const trimmed = q.trim().slice(0, MAX_QUERY);
    if (trimmed.length > 0) search.q = trimmed;
  }

  const thread = raw['thread'];
  if (typeof thread === 'string' && thread.length > 0) search.thread = thread;

  if (raw['view'] === 'notifications') search.view = 'notifications';

  return search;
}

/**
 * THE ROUTER HAS ALREADY PARSED THE VALUE BY THE TIME THIS SEES IT.
 *
 * `?unread` with no value arrives as `''`, `?unread=true` as `'true'`, and a checkbox that wrote
 * a boolean arrives as `true`. All three mean the same thing and all three are accepted; anything
 * else is somebody typing in the address bar and means the default list.
 */
function isTruthy(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value !== 'string') return false;
  return TRUTHY.includes(value.trim().toLowerCase());
}

/** The filter as the list component asks about it. */
export function unreadOnly(search: InboxSearch): boolean {
  return search.unread === true;
}

/** «Direktemeldinger»-fanen, spurt slik lista spør. */
export function dmOnly(search: InboxSearch): boolean {
  return search.dm === true;
}

/** «Arkiverte»-fanen. Standardsvaret fra ruten er de ÅPNE — se kontrakten. */
export function archivedOnly(search: InboxSearch): boolean {
  return search.closed === true;
}

/** «Bookinger i organisasjonen» (0931, 84): det du har tilgang til uten å følge. */
export function bookingsOnly(search: InboxSearch): boolean {
  return search.bookings === true;
}

/**
 * True when the list has been narrowed — which changes the empty copy and its exit.
 *
 * SØKET TELLER MED. En tom liste etter et søk skal si «ingen treff», ikke «du har ingen
 * samtaler» — og det er den setningen denne funksjonen velger.
 */
export function isFiltered(search: InboxSearch): boolean {
  return (
    unreadOnly(search) ||
    dmOnly(search) ||
    archivedOnly(search) ||
    bookingsOnly(search) ||
    search.q !== undefined
  );
}

/**
 * FANEN SOM ER VALGT, og hvilken adresse hver av dem fører til.
 *
 * Fire faner, ett valg: å velge én fjerner de to andre. Adressen tåler likevel flere samtidig —
 * «uleste direktemeldinger» er en adresse man kan skrive, og lista svarer riktig — men menyen
 * setter én om gangen, fordi fire knapper som kan kombineres er seksten tilstander å forstå.
 *
 * SØKET OG DEN VALGTE TRÅDEN OVERLEVER ET FANEBYTTE. Det er den ene detaljen som gjør fanene
 * brukbare: å bytte til «Uleste» mens man har skrevet «kapabel» skal snevre det man ser på, ikke
 * kaste det man skrev.
 */
export type InboxTab = 'all' | 'unread' | 'dm' | 'archived' | 'bookings';

export function activeTab(search: InboxSearch): InboxTab {
  if (unreadOnly(search)) return 'unread';
  if (dmOnly(search)) return 'dm';
  if (archivedOnly(search)) return 'archived';
  if (bookingsOnly(search)) return 'bookings';
  return 'all';
}

export function withTab(search: InboxSearch, tab: InboxTab): InboxSearch {
  const next: InboxSearch = {};
  if (search.q !== undefined) next.q = search.q;
  if (search.thread !== undefined) next.thread = search.thread;
  if (tab === 'unread') next.unread = true;
  if (tab === 'dm') next.dm = true;
  if (tab === 'archived') next.closed = true;
  if (tab === 'bookings') next.bookings = true;
  return next;
}

/** Den samme adressen uten den gamle `?view=notifications` (276.10). */
export function withoutLegacyView(search: InboxSearch): InboxSearch {
  const next: InboxSearch = { ...search };
  delete next.view;
  return next;
}

/** The next URL for the unread toggle. Absent rather than `false`, so there is one clean URL. */
export function withUnread(search: InboxSearch, unread: boolean): InboxSearch {
  const next: InboxSearch = { ...search };
  if (unread) next.unread = true;
  else delete next.unread;
  return next;
}

/** Søketeksten i adressen. Tom streng skrives ikke — den er den samme lista under to adresser. */
export function withQuery(search: InboxSearch, q: string): InboxSearch {
  const next: InboxSearch = { ...search };
  const trimmed = q.trim();
  if (trimmed.length > 0) next.q = trimmed.slice(0, MAX_QUERY);
  else delete next.q;
  /* Et nytt søk beholder ikke den valgte tråden: den er sjelden i treffene. */
  delete next.thread;
  return next;
}

/** Den valgte tråden. `null` lukker høyrekolonnen uten å røre filtrene. */
export function withThread(search: InboxSearch, threadId: string | null): InboxSearch {
  const next: InboxSearch = { ...search };
  if (threadId === null) delete next.thread;
  else next.thread = threadId;
  return next;
}
